import { CheckoutSession, getCheckoutSessionByHppId } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';

import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { useCache } from '@/hooks/requests/useCache';

interface UseHppPollingProps {
  hppId: string;
  checkoutSession?: CheckoutSession;
}

const MAX_ALLOWED_POLLING_COUNTER_TRYOUTS = 15;
const CHECKOUT_SESSION_POLLING_TIMEOUT = 2000;

export const useHppDistributionPolling = ({ hppId, checkoutSession }: UseHppPollingProps) => {
  const {
    state: { pollingCounter },
    actions: { updateApplicationContext },
  } = useApplicationContext();
  const { revalidate } = useCache(getCheckoutSessionByHppId, { hppId });

  useEffect(() => {
    if (!checkoutSession?.hpp || pollingCounter === 0) return;

    const areAllDistributionsSent =
      checkoutSession.hpp.distributions.length > 0 &&
      checkoutSession.hpp.distributions.every((distribution) => !!distribution.sentAt);

    if (areAllDistributionsSent || pollingCounter >= MAX_ALLOWED_POLLING_COUNTER_TRYOUTS) {
      updateApplicationContext({ pollingCounter: 0 });
      return;
    }

    if (!areAllDistributionsSent && pollingCounter > 0) {
      try {
        setTimeout(() => {
          asyncRevalidateCheckoutSessionData();
          updateApplicationContext({ pollingCounter: pollingCounter + 1 });
        }, CHECKOUT_SESSION_POLLING_TIMEOUT);
      } catch (error) {}
    }
  }, [pollingCounter, checkoutSession]);

  const retryHppPolling = () => {
    updateApplicationContext({ pollingCounter: 0 });
    asyncRevalidateCheckoutSessionData();
  };

  const asyncRevalidateCheckoutSessionData = async () => {
    await revalidate();
  };

  return { pollingCounter, retryHppPolling };
};
